import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Upload, message, notification } from 'antd';
import type { UploadProps } from 'antd';
import {
  ArrowLeftOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import Locations from '@/components/Locations/Locations/Locations';
import LocationService from '@/domain/Location/LocationService';
import ParameterFileService from '../../../../domain/ParameterFiles/ParameterFilesService';
import { Modal } from '@hydrodigital/design-system';

const parameterFileService = new ParameterFileService();
const { Dragger } = Upload;
function UploadParametersFilesView() {
  const navigate = useNavigate();
  const [messageApi] = message.useMessage();
  const [files, setFiles] = useState<any>(null);
  const [hasLocation, setHasLocation] = useState<boolean>(false);
  const [locations, setLocations] = useState<any[]>([]);
  const [location, setLocation] = useState<any>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorContent, setErrorContent] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ message: string } | null>(null);
  const locationService = new LocationService();

  useEffect(() => {
    const userId = localStorage.getItem('userId') ?? '';
    const resLocationByUser = locationService.getLocationsByUser(userId);
    resLocationByUser.then((value) => {
      setLocations(value);
    });
  }, []);

  const initialUploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    accept: '.xlsx',
    showUploadList: false,
    beforeUpload: (file: any) => {
      if (files && !location) {
        notification.error({
          message: 'Error',
          description:
            'Please select the location for the current file before uploading another.',
        });
        return false; // Prevent the new file from being uploaded
      }

      const nameSplit = file.name.split('.');
      if (file.name.length > 100) {
        messageApi.open({
          type: 'error',
          content: `File name ${file.name} exceeds 100 characters`,
        });
        notification.error({
          message: 'Error',
          description: `File name ${file.name} exceeds 100 characters`,
        });
        return false;
      }
      const extension = nameSplit[nameSplit.length - 1];
      if (extension === 'xlsx') {
        setFiles(file);
      } else {
        showErrorFile();
      }
      return true;
    },
  };

  const showErrorFile = () => {
    messageApi.open({
      type: 'error',
      content: 'One of the files selected is not valid',
    });
  };

  const deleteFile = (fileId: any) => {
    // const newArray = files.filter((f) => f.uid !== fileId);
    setFiles(null);
  };

  const blobToBase64 = async (blob: any): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => {
        if (reader.result) {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        } else {
          resolve('');
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const uploadFiles = async () => {
    if (files.length == 0) {
      openNotificationNoDat();
      return;
    }
    if (!location) {
      notification.warning({
        message: 'Warning',
        description:
          'Please select a location for the current file before uploading.',
      });
      return;
    }

    let dataFiles = {
      filename: files.name,
      file: await blobToBase64(files),
      locationId: location,
    };

    try {
      const response = await parameterFileService.postParameterFiles(dataFiles);
      if (response.success == 1) {
        const uploads = JSON.parse(
          localStorage.getItem('parameterUploads') ?? '[]',
        );
        uploads.push({ id: response.message });
        localStorage.setItem('parameterUploads', JSON.stringify(uploads));
        setSuccessInfo({
          message: response.message || 'Upload successful',
        });
        setShowSuccessModal(true);
      } else if (response.success == 3) {
        setErrorContent(response.message || 'An error occurred while uploading the file.');
        setShowErrorModal(true);
      }
    } catch (error: any) {
      const errData = error?.response?.data;
      const msg =
        typeof errData === 'string'
          ? errData
          : errData?.message || errData?.body?.message || error?.message || 'An error occurred while uploading the file.';
      setErrorContent(msg);
      setShowErrorModal(true);
    }
  };

  const openNotificationNoDat = () => {
    notification.open({
      message: 'Error',
      description: 'At least you need to upload one file.',
      type: 'error',
    });
  };

  const handleClickSelect = (value: any) => {
    setLocation(value);
    setHasLocation(true);
  };

  const fileSize = (size: any) => {
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return `${(size / Math.pow(k, i)).toFixed(3)} ${sizes[i]}`;
  };

  return (
    <div style={{ padding: '0px 3rem' }}>
      <div>
        <Button
          type="text"
          shape="round"
          className="flex items-center"
          style={{ backgroundColor: '#F7F9FA', color: '#434B56' }}
          onClick={() => {
            navigate(`/upload?selectedTab=PARAMETER`);
          }}
        >
          <ArrowLeftOutlined rev={undefined} /> Back to Parameter Files
        </Button>
      </div>
      <div className="flex column pt-2">
        <span className="text-title-page">Upload Parameters Files</span>
        <div
          className="mt-2 flex items-center justify-between"
          style={{
            background: 'rgba(255, 238, 203, 0.5)',
            height: '40px',
            borderLeft: 'solid 8px #FBC261',
          }}
        >
          <span className="ml-2" style={{ color: '#023E65' }}>
            Only .xlsx files accepted
          </span>
          <a
            href={`${import.meta.env.VITE_AZURE_CDN}assets/docs/BulkConfig_Template.xlsx`}
            download
            className="mr-2"
            style={{ color: '#023E65', textDecoration: 'underline', fontSize: '14px' }}
          >
            Download template
          </a>
        </div>
        <div className="tableContainer mt-3 flex column w-full">
          <table
            style={{
              border: '2px solid #E6E6E6',
              height: 'calc(100vh - 340px)',
              borderRadius: '10px',
              borderCollapse: 'separate',
            }}
          >
            <thead>
              <tr style={{ height: '50px' }}>
                <th
                  style={{
                    paddingLeft: '20px',
                    borderBottom: '2px solid #E6E6E6',
                  }}
                >
                  File Name
                </th>
                <th
                  style={{
                    borderBottom: '2px solid #E6E6E6',
                  }}
                >
                  Location
                </th>
                <th
                  style={{
                    borderBottom: '2px solid #E6E6E6',
                  }}
                >
                  Size
                </th>
                <th
                  style={{
                    borderBottom: '2px solid #E6E6E6',
                  }}
                ></th>
              </tr>
            </thead>
            {!files ? (
              <tbody>
                <tr>
                  <td colSpan={4}>
                    <Dragger
                      {...initialUploadProps}
                      style={{ backgroundColor: 'white' }}
                    >
                      <div className="flex column" style={{}}>
                        <span className="mb-2" style={{ color: '#74788D' }}>
                          Drag and drop your files here or click in
                        </span>
                        {/* <Upload {...uploadProps}> */}
                        <Button
                          icon={<PlusCircleOutlined rev={undefined} />}
                          shape="round"
                          className="flex items-center self-center"
                          style={{
                            color: '#023E65',
                            borderColor: '#023E65',
                            backgroundColor: 'transparent',
                          }}
                        >
                          Add Files
                        </Button>
                        {/* </Upload> */}
                      </div>
                    </Dragger>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                <tr
                  style={{
                    height: '40px',
                    backgroundColor: '#F7F9FC',
                  }}
                  key={files.uid}
                >
                  <td style={{ paddingLeft: '50px' }}>{files.name}</td>
                  <td style={{ paddingLeft: '150px' }}>
                    {' '}
                    <Locations
                      locations={locations}
                      handleClickSelect={(value) => handleClickSelect(value)}
                      defaultValue={false}
                    />
                  </td>
                  <td>{fileSize(files.size)}</td>
                  <td>
                    <DeleteOutlined
                      style={{
                        cursor: 'pointer',
                        fontSize: '1.3rem',
                      }}
                      onClick={() => {
                        deleteFile(files.uid);
                      }}
                      rev={undefined}
                    />
                  </td>
                </tr>
                <tr>
                  <td colSpan={5}>
                    <Dragger
                      {...initialUploadProps}
                      style={{ backgroundColor: 'white' }}
                    >
                      <Button
                        icon={
                          <PlusCircleOutlined
                            style={{
                              fontSize: '1.4rem',
                            }}
                            rev={undefined}
                          />
                        }
                        shape="round"
                        style={{
                          width: '50px',
                          height: '50px',
                          color: '#023E65',
                          border: '2px solid #023E65',
                          bottom: '10px',
                          right: '10px',
                        }}
                        className="flex items-center justify-center absolute"
                      />
                    </Dragger>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
        <div className="flex pt-3 justify-between">
          <Button
            style={{ width: '85px', height: '40px' }}
            onClick={() => {
              navigate(`/upload?selectedTab=PARAMETERS`);
            }}
          >
            Cancel
          </Button>
          <div>
            <Button
              style={{
                width: '120px',
                height: '40px',
                backgroundColor: '#00203C',
                color: 'white',
              }}
              disabled={!location}
              onClick={() => {
                uploadFiles();
              }}
            >
              Upload Files
            </Button>
          </div>
        </div>
      </div>
      <Modal
        isOpen={showErrorModal}
        size="m"
        closeOnOverlayClick={true}
        closeOnEscape={true}
        showCloseButton={true}
        onClose={() => setShowErrorModal(false)}
      >
        <Modal.Header>
          <span style={{ color: '#232B36', fontSize: '1.1rem', fontWeight: 'bold' }}>
            Upload Error
          </span>
        </Modal.Header>
        <Modal.Content>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: '#74788D',
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              margin: 0,
              minHeight: '100px',
            }}
          >
            {errorContent}
          </pre>
        </Modal.Content>
        <div className="separator mb-2 mt-2" />
        <Modal.Footer>
          <Button
            onClick={() => setShowErrorModal(false)}
            style={{
              width: '85px',
              height: '40px',
              backgroundColor: '#00203C',
              color: 'white',
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        isOpen={showSuccessModal}
        size="s"
        closeOnOverlayClick={false}
        closeOnEscape={false}
        showCloseButton={false}
        onClose={() => {}}
      >
        <Modal.Header>
          <span style={{ color: '#232B36', fontSize: '1.1rem', fontWeight: 'bold' }}>
            Upload Successful
          </span>
        </Modal.Header>
        <Modal.Content>
          <span style={{ color: '#74788D', fontSize: '1rem', display: 'block', minHeight: '100px' }}>
            {successInfo?.message}
          </span>
        </Modal.Content>
        <div className="separator mb-2 mt-2" />
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowSuccessModal(false);
              navigate(`/upload?selectedTab=PARAMETER`);
            }}
            style={{
              width: '85px',
              height: '40px',
              backgroundColor: '#00203C',
              color: 'white',
            }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function mapState(state: { locations: { array: any } }) {
  return {
    locations: state.locations.array,
  };
}

export default connect(mapState)(UploadParametersFilesView);
